import type { Session } from "../api/models";
import type { DataViewLayout } from "../ui/PagedDataView";
import { PButton } from "../ui/PButton";
import { carded, egmTheoNumber, theoNumber, uncarded, type ActualDisplay } from "./money";
import { formatDuration } from "./quick-filters";

// PORT OF `paged-data-view/_templates/session-template` AND THE `_parts` IT USES, CLASS
// FOR CLASS. THE GRID CARD IS `p-2 border-1 surface-border surface-card border-round`;
// THE LIST ROW HAS NO CARD (THE DATA VIEW'S ROW WRAPPER SPACES IT) AND TIGHTER `mt-1` ROWS.
export function SessionCard(props: {
  session: Session;
  layout: DataViewLayout;
  onProfile: () => void;
  onActions: () => void;
  onLocation: () => void;
}) {
  const { session: s, layout, onProfile, onActions, onLocation } = props;
  const grid = layout === "grid";

  // LIST LAYOUT COLORS THE CARD ICON BY HOST; THE GRID CARD IS ALWAYS PRIMARY
  const cardIconClass = grid ? "color-primary" : s.hostId === 0 ? "color-gray-500" : "color-primary";

  const body = (
    <div>
      <div className="flex flex-row align-items-center justify-content-between">
        <div className={`flex flex-row align-items-center${grid ? " justify-content-start" : ""}`}>
          {s.cmsPlayerId ? (
            <span className={`material-symbols-rounded ${cardIconClass}`}>credit_card</span>
          ) : (
            <span className="material-symbols-rounded">credit_card_off</span>
          )}
          <button type="button" className="text-overflow-ellipsis font-bold ml-3 hover-link" onClick={onProfile}>
            {(s.playerName ?? "").toUpperCase()}
          </button>
        </div>
        <div className="flex flex-column justify-content-end align-items-end">
          <PButton icon="ellipsis-v" rounded variant="text" severity="secondary" ariaLabel="Actions" onClick={onActions} />
        </div>
      </div>

      {s.siteName && (
        <div className="mt-1">
          <span className="text-sm font-bold">{s.siteName}</span>
        </div>
      )}

      <div className={`${grid ? "mt-2" : "mt-1"} w-full flex flex-row align-items-center justify-content-between gap-2`}>
        <div className="text-overflow-ellipsis text-sm">{s.description}</div>
        <div>
          <PButton label={s.location} icon="map-marker" className="font-bold min-width-100" onClick={onLocation} sx={{ fontWeight: 700 }} />
        </div>
      </div>

      <div className={`${grid ? "mt-3" : "mt-1"} flex flex-row align-items-center justify-content-between`}>
        <SessionMoney session={s} carded={!!s.isCarded} />
      </div>
    </div>
  );

  return grid ? <div className="p-2 border-1 surface-border surface-card border-round flex flex-column">{body}</div> : body;
}

// THE DURATION / ACTUAL / THEO TRIO. THE CARD DECIDES "CARDED" BY `isCarded`; THE SELECTED-SESSION
// SIDEBAR BY `cmsPlayerId`, AS THEIR ANGULAR TEMPLATES DO.
export function SessionMoney({ session: s, carded: isCarded }: { session: Session; carded: boolean }) {
  return (
    <>
      <Duration seconds={s.duration} />
      {isCarded ? <Actual value={carded(s.totalDailyActual)} /> : <Actual value={uncarded(s.gains)} large />}
      {isCarded ? (
        // `total-daily-theo`: "Theo:" THEN A `ml-2` "$" THEN THE NUMBER, BOLD. NO SPACE BETWEEN THE
        // SPANS: ANGULAR STRIPS THAT WHITESPACE-ONLY TEXT NODE, SO THE GAP IS THE MARGIN ALONE
        <div className="font-bold">
          <span>Theo:</span>
          <span className="ml-2">$</span>
          {theoNumber(s.totalDailyTheo)}
        </div>
      ) : (
        // `egm-theo-win`: BOLD `text-lg`, NO THOUSANDS SEPARATOR
        <div className="font-bold text-lg">
          <span>Theo: $</span>
          {egmTheoNumber(s.theoWin)}
        </div>
      )}
    </>
  );
}

// `_parts/duration`: THE `avg_pace` ICON THEN `ml-2` TEXT
function Duration({ seconds }: { seconds: number | undefined }) {
  return (
    <div className="flex flex-row align-items-center">
      <span className="material-symbols-rounded">avg_pace</span>
      <div className="ml-2">{formatDuration(seconds)}</div>
    </div>
  );
}

// `_parts/total-daily-actual` (BOLD) / `_parts/gains` (BOLD `text-lg`): A 24PX ARROW IN
// `text-green-600`/`text-red-600`, THEN THE AMOUNT IN THE SAME COLOR
function Actual({ value, large = false }: { value: ActualDisplay | null; large?: boolean }) {
  if (!value) return <div />;
  const tone = value.tone === "up" ? "text-green-600" : "text-red-600";
  return (
    <div className={`flex flex-row align-items-center font-bold${large ? " text-lg" : ""}`}>
      {value.arrow && <span className={`material-symbols-rounded ${tone}`}>{value.tone === "up" ? "arrow_upward" : "arrow_downward"}</span>}
      <div className={tone}>{value.text}</div>
    </div>
  );
}

